import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuthState } from "react-redux-django-auth";
import { authClientWeb } from "react-redux-django-auth/web";
import {
  FileText,
  CheckCircle2,
  AlertCircle,
  MessageCircle,
  ExternalLink,
  Code2,
  BookOpen,
  ArrowRight,
} from "lucide-react";
import Button from "../../../components/common/Button";

const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb is not initialized yet
  }
  return axios.create();
};

const AssignmentRow = ({
  title,
  programLabel,
  moduleTitle,
  dueDate,
  dueRelative,
  status,
  score,
  feedback = null,
  requiresAutograder,
  codespaceUrl,
  repositoryUrl,
  onOpenAssignment,
}: any) => {
  const getStatusStyles = (s: string) => {
    switch (s) {
      case "Graded":
        return "cht-bg-green-100 cht-text-green-700";
      case "Pending":
        return "cht-bg-orange-100 cht-text-orange-700";
      case "Overdue":
        return "cht-bg-red-100 cht-text-red-700";
      default:
        return "cht-bg-gray-100 cht-text-gray-700";
    }
  };

  return (
    <div className="cht-group cht-space-y-4">
      <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-4 cht-p-6 cht-bg-white cht-rounded-[24px] cht-border cht-border-gray-100 hover:cht-border-[#3e57da]/30 hover:cht-shadow-[0_10px_30px_rgba(0,0,0,0.03)] cht-transition-all cht-duration-300">
        <div className="cht-flex cht-items-center">
          <div className="cht-p-4 cht-bg-gray-50 cht-rounded-[18px] cht-mr-5 group-hover:cht-bg-[#3e57da]/10 cht-transition-colors">
            {requiresAutograder ? (
              <Code2 size={24} className="cht-text-[#3e57da]" />
            ) : (
              <FileText size={24} className="cht-text-[#3e57da]" />
            )}
          </div>
          <div className="cht-space-y-1">
            <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2">
              <span className="cht-text-[10px] cht-font-bold cht-text-[#3e57da] cht-uppercase cht-tracking-wider">
                {programLabel}
              </span>
              {moduleTitle && (
                <>
                  <span className="cht-text-gray-300">•</span>
                  <span className="cht-text-[10px] cht-font-semibold cht-text-gray-500">
                    {moduleTitle}
                  </span>
                </>
              )}
            </div>
            <h5 className="cht-font-bold cht-text-black group-hover:cht-text-[#3e57da] cht-transition-colors">
              {title}
            </h5>
            <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3 cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest">
              <span>Due: {dueDate}</span>
              {dueRelative && dueRelative !== dueDate && (
                <span>({dueRelative})</span>
              )}
              {status === "Overdue" && (
                <span className="cht-text-red-500 cht-flex cht-items-center cht-gap-1">
                  <AlertCircle size={10} /> Urgent
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3 sm:cht-gap-4">
          {score != null && (
            <span className="cht-px-3 cht-py-1.5 cht-text-xs cht-font-black cht-rounded-full cht-bg-indigo-50 cht-text-[#3e57da]">
              {score}%
            </span>
          )}
          <span
            className={`cht-px-4 cht-py-1.5 cht-text-[10px] cht-font-bold cht-rounded-full cht-uppercase cht-tracking-wider ${getStatusStyles(
              status
            )}`}
          >
            {status}
          </span>

          {codespaceUrl && (
            <Button
              as="a"
              href={codespaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              size="sm"
              isPill
              rightIcon={<ExternalLink size={13} />}
            >
              Codespace
            </Button>
          )}

          {status !== "Graded" ? (
            <Button
              variant="secondary"
              size="sm"
              isPill
              className="cht-px-6"
              rightIcon={<ArrowRight size={14} />}
              onClick={onOpenAssignment}
            >
              {requiresAutograder ? "Open Workspace" : "Open Task"}
            </Button>
          ) : (
            <Button
              variant="tab"
              isPill
              size="sm"
              className="cht-p-2.5 cht-min-w-0"
              onClick={onOpenAssignment}
              title="View in Course"
            >
              <CheckCircle2 size={18} />
            </Button>
          )}
        </div>
      </div>

      {feedback && (
        <div className="cht-ml-4 sm:cht-ml-16 cht-p-5 cht-bg-[#3e57da]/5 cht-border-l-4 cht-border-[#3e57da] cht-rounded-r-[18px] cht-rounded-bl-[18px] cht-flex cht-gap-4 cht-animate-in cht-slide-in-from-left-2 cht-duration-500">
          <MessageCircle size={18} className="cht-text-[#3e57da] cht-shrink-0 cht-mt-1" />
          <div className="cht-space-y-1">
            <p className="cht-text-[10px] cht-font-bold cht-text-[#3e57da] cht-uppercase cht-tracking-widest">
              Instructor Feedback
            </p>
            <p className="cht-text-sm cht-font-medium cht-text-gray-700 leading-relaxed italic">
              "{feedback}"
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const Assignments = () => {
  const { isAuthenticated, isAuthLoading } = useAuthState();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<"all" | "pending" | "graded">("all");
  const [assignments, setAssignments] = useState<any[]>([]);
  const [summary, setSummary] = useState({
    total_assigned: 0,
    pending_count: 0,
    overdue_count: 0,
    graded_count: 0,
    average_score: 0,
    average_grade: "—",
  });

  useEffect(() => {
    if (isAuthLoading) return;
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    let isMounted = true;
    const loadAssignments = async () => {
      try {
        setIsLoading(true);
        const client = getClient();
        const res = await client.get("/course/bk-learning-analytics/");
        if (isMounted && res.data) {
          if (Array.isArray(res.data.assignments)) {
            setAssignments(res.data.assignments);
          }
          if (res.data.assignments_summary) {
            setSummary((prev) => ({ ...prev, ...res.data.assignments_summary }));
          }
        }
      } catch (err) {
        console.error("Error loading student assignments:", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    loadAssignments();
    return () => {
      isMounted = false;
    };
  }, [isAuthLoading, isAuthenticated]);

  const filteredAssignments = assignments.filter((a) => {
    if (filterStatus === "pending") return a.status === "Pending" || a.status === "Overdue";
    if (filterStatus === "graded") return a.status === "Graded";
    return true;
  });

  return (
    <div className="cht-space-y-10 cht-animate-in cht-fade-in cht-duration-700">
      {/* Status at a Glance */}
      <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-3 cht-gap-6">
        {[
          {
            label: "Total Assigned",
            value: isLoading ? "..." : String(summary.total_assigned),
            sub: `${summary.graded_count} completed`,
            color: "cht-text-[#3e57da]",
          },
          {
            label: "Pending Submission",
            value: isLoading ? "..." : String(summary.pending_count),
            sub:
              summary.overdue_count > 0
                ? `${summary.overdue_count} overdue`
                : "Up to date",
            color: "cht-text-orange-500",
          },
          {
            label: "Average Grade",
            value: isLoading ? "..." : summary.average_grade || "—",
            sub:
              summary.graded_count > 0
                ? `${summary.average_score}% avg score`
                : "No graded tasks yet",
            color: "cht-text-green-600",
          },
        ].map((stat, i) => (
          <div
            key={i}
            className="cht-bg-white cht-p-8 cht-rounded-[28px] cht-shadow-[0_10px_30px_rgba(0,0,0,0.02)] cht-border cht-border-gray-50 cht-flex cht-flex-col cht-items-center cht-text-center cht-space-y-2"
          >
            <p className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-[0.2em]">
              {stat.label}
            </p>
            <h3 className={`cht-text-4xl cht-font-black ${stat.color}`}>
              {stat.value}
            </h3>
            <p className="cht-text-xs cht-font-semibold cht-text-gray-400">
              {stat.sub}
            </p>
          </div>
        ))}
      </div>

      {/* Assignment List */}
      <section className="cht-space-y-6 cht-pb-12">
        <div className="cht-flex cht-flex-wrap cht-items-center cht-justify-between cht-gap-4 cht-px-2">
          <h2 className="cht-text-xl cht-font-bold cht-text-black">
            Course Tasks & Exercises
          </h2>
          <div className="cht-flex cht-items-center cht-gap-2 cht-bg-white cht-p-1 cht-rounded-full cht-border cht-border-gray-100">
            {[
              { id: "all", label: `All (${assignments.length})` },
              { id: "pending", label: `Pending (${summary.pending_count})` },
              { id: "graded", label: `Completed (${summary.graded_count})` },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilterStatus(tab.id as any)}
                className={`cht-px-4 cht-py-1.5 cht-rounded-full cht-text-xs cht-font-bold cht-transition-all ${
                  filterStatus === tab.id
                    ? "cht-bg-[#3e57da] cht-text-white"
                    : "cht-text-gray-500 hover:cht-text-gray-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="cht-space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="cht-h-24 cht-bg-white cht-rounded-[24px] cht-border cht-border-gray-100 cht-animate-pulse"
              />
            ))}
          </div>
        ) : filteredAssignments.length > 0 ? (
          <div className="cht-space-y-6">
            {filteredAssignments.map((item) => (
              <AssignmentRow
                key={item.id}
                title={item.title}
                programLabel={item.program_label}
                moduleTitle={item.module_title}
                dueDate={item.due_date}
                dueRelative={item.due_relative}
                status={item.status}
                score={item.score}
                feedback={item.feedback}
                requiresAutograder={item.requires_autograder}
                codespaceUrl={item.codespace_url}
                repositoryUrl={item.repository_url}
                onOpenAssignment={() =>
                  navigate(
                    `/student/dashboard/courses/${item.course_slug}/enrolled?courseId=${item.course_id}`
                  )
                }
              />
            ))}
          </div>
        ) : (
          <div className="cht-bg-white cht-rounded-[28px] cht-p-16 cht-border cht-border-gray-100 cht-flex cht-flex-col cht-items-center cht-text-center cht-space-y-4">
            <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-rounded-full cht-flex cht-items-center cht-justify-center">
              <BookOpen className="cht-text-gray-400" size={28} />
            </div>
            <div className="cht-space-y-1">
              <h4 className="cht-text-lg cht-font-bold cht-text-gray-900">
                No tasks found
              </h4>
              <p className="cht-text-sm cht-text-gray-500 cht-max-w-sm">
                {assignments.length === 0
                  ? "Exercises and coding tasks from your enrolled courses will appear here automatically."
                  : "No tasks match the selected filter."}
              </p>
            </div>
            {assignments.length === 0 && (
              <Button
                variant="secondary"
                isPill
                onClick={() => navigate("/student/dashboard/courses")}
              >
                Browse Course Library
              </Button>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default Assignments;

